import {AxiosResponse} from 'axios'
import {unwrapAPIError} from 'utils'
import {
  EndpointsConfigTypes,
  ListInvitedAdminsTypes,
  UsersGroupsTypes,
  ExceptionType,
  SubscriptionDetailsType,
  AuditLogsType,
  AuditLogsRequestType,
  RESOURCE_TYPE_SINGULAR,
  UnknownObject,
} from 'interfaces'
import {requestHandler, getAuditBaseUrl} from '../HTTP'
import {EndPoints, HttpMethods} from '../HTTP/HTTP.types'
import {
  DefaultBlockPagePayload,
  SearchUsersGroupsItem,
  GetImportStatusResponse,
  UpdateCustomBlockPages,
  UsersAndGroupsLastEvaluatedKeyTypes,
  GetUserGroupDetailsResponse,
  UpdateEndpointsConfigAction,
  BlockPagesResponse,
} from '../../../interfaces/Settings.types'

const getLink = async (): Promise<
  AxiosResponse<{
    url: string
    message: string
    notifier_type: string
    sns_topic: string
  }>
> => {
  try {
    const res = await requestHandler<{
      url: string
      message: string
      notifier_type: string
      sns_topic: string
    }>({
      method: HttpMethods.GET,
      url: EndPoints.GetLink,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getImportStatus = async (): Promise<
  AxiosResponse<GetImportStatusResponse>
> => {
  try {
    const response = await requestHandler<GetImportStatusResponse>({
      method: HttpMethods.GET,
      url: EndPoints.GetImportStatus,
    })

    return response
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getUsers = async (data: {
  qs?: UsersAndGroupsLastEvaluatedKeyTypes
}): Promise<
  AxiosResponse<{
    message: string
    body: {
      Items: {
        emailId: string
        name: string
        groupNames: string[]
      }[]
      LastEvaluatedKey: {
        emailId: string
        tenantId: string
      }
    }
  }>
> => {
  try {
    const res = await requestHandler<{
      message: string
      body: {
        Items: {
          emailId: string
          name: string
          groupNames: string[]
        }[]
        LastEvaluatedKey: {
          emailId: string
          tenantId: string
        }
      }
    }>({
      method: HttpMethods.POST,
      url: EndPoints.GetUsers,
      data: data.qs ? {LastEvaluatedKey: data.qs} : {},
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getGroups = async (data: {
  qs?: UsersAndGroupsLastEvaluatedKeyTypes
}): Promise<
  AxiosResponse<{
    message: string
    body: {
      Items: {
        membersCount: string
        emailId: string
        name: string
      }[]
      LastEvaluatedKey: {
        emailId: string
        tenantId: string
      }
    }
  }>
> => {
  try {
    const res = await requestHandler<{
      message: string
      body: {
        Items: {
          membersCount: string
          emailId: string
          name: string
        }[]
        LastEvaluatedKey: {
          emailId: string
          tenantId: string
        }
      }
    }>({
      method: HttpMethods.POST,
      url: EndPoints.GetGroups,
      data: data.qs ? {LastEvaluatedKey: data.qs} : {},
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getSearchUsersGroups = async (data: {
  text: string
  models: string[]
}): Promise<AxiosResponse<SearchUsersGroupsItem[]>> => {
  try {
    const res = await requestHandler<{
      message: string
      Items: UsersGroupsTypes
    }>({
      method: HttpMethods.POST,
      url: EndPoints.SearchUsersGroups,
      data: data,
    })

    const Items = [
      {
        label: RESOURCE_TYPE_SINGULAR.User,
        options: [
          ...(res?.data?.Items?.Users || []).map(obj => ({
            ...obj,
            type: ExceptionType.User,
          })),
        ],
      },
      {
        label: RESOURCE_TYPE_SINGULAR.Group,
        options: [
          ...(res?.data?.Items?.Groups || []).map(obj => ({
            ...obj,
            type: ExceptionType.Group,
          })),
        ],
      },
    ]

    const normalizedResponse: AxiosResponse<SearchUsersGroupsItem[]> = {
      ...res,
      data: [...Items],
    }

    return normalizedResponse
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getInvitedAdmin = async (): Promise<
  AxiosResponse<{Items: ListInvitedAdminsTypes[]}>
> => {
  try {
    const res = await requestHandler<{Items: ListInvitedAdminsTypes[]}>({
      method: HttpMethods.POST,
      url: EndPoints.GetAdminInvites,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const inviteAdmins = async (data: {
  emailId: string[]
  role: string
}): Promise<AxiosResponse<{message: string}>> => {
  try {
    return await requestHandler<{message: string}, typeof data>({
      method: HttpMethods.POST,
      url: EndPoints.InviteAdmin,
      data,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateInvitedAdmins = async (data: {
  emailId: string
  role: string
}): Promise<AxiosResponse<{message: string}>> => {
  try {
    const res = await requestHandler<{message: string}, typeof data>({
      method: HttpMethods.PUT,
      url: EndPoints.UpdateAdmin,
      data,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const verifyAdminInvite = async (
  token: string,
): Promise<AxiosResponse<{message: string}>> => {
  try {
    const res = await requestHandler<{message: string}>({
      method: HttpMethods.POST,
      url: `${EndPoints.VerifyAdminInvite}?token=${token}` as unknown as EndPoints,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const deleteAdmin = async (
  emailId: string,
): Promise<AxiosResponse<{message: string}>> => {
  try {
    const res = await requestHandler<{message: string}>({
      method: HttpMethods.POST,
      url: EndPoints.DeleteAdmin,
      data: {emailId},
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getBlockPages = async (): Promise<AxiosResponse<BlockPagesResponse>> => {
  try {
    const res = await requestHandler<BlockPagesResponse>({
      method: HttpMethods.GET,
      url: EndPoints.GetBlockPages,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getEndpointsConfig = async (): Promise<
  AxiosResponse<EndpointsConfigTypes>
> => {
  try {
    const res = await requestHandler<EndpointsConfigTypes>({
      method: HttpMethods.GET,
      url: EndPoints.SettingsEndpointsConfig,
    })

    // TODO: Remove this once the backend is updated to not return this field
    // https://github.com/dopesecurity/cloud-backend/issues/3977
    return removeFieldFromResponse(res, 'delegatedAuthConfigured')
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

/**
 * Utility function to remove specific fields from API responses
 * @param response The original API response
 * @param fieldToRemove The field to remove from the response data
 * @returns A new response object with the specified field removed
 */
const removeFieldFromResponse = <T extends Record<string, unknown>>(
  response: AxiosResponse<T>,
  fieldToRemove: string,
): AxiosResponse<T> => {
  if (response?.data && fieldToRemove in response.data) {
    const {[fieldToRemove]: _, ...restData} = response.data
    return {
      ...response,
      data: restData as T,
    }
  }
  return response
}

const updateDefaultBlockPages = async (
  payload: DefaultBlockPagePayload,
): Promise<AxiosResponse<{message: string}>> => {
  try {
    const res = await requestHandler<{message: string}>({
      method: HttpMethods.POST,
      url: EndPoints.UpdateDefaultBlockPages,
      // any type is okay here as request handler type is to generic with record
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      data: payload as any,
    })

    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateEndpointsConfig = async (
  data: UpdateEndpointsConfigAction,
): Promise<AxiosResponse<EndpointsConfigTypes>> => {
  try {
    const res = await requestHandler<EndpointsConfigTypes>({
      method: HttpMethods.PUT,
      url: EndPoints.SettingsEndpointsConfig,
      // Incompatible type on requestHandler
      data: data as UnknownObject,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const updateCustomBlockPages = async (
  data: UpdateCustomBlockPages,
): Promise<AxiosResponse<{message: string}>> => {
  try {
    const res = await requestHandler<{message: string}>({
      method: HttpMethods.POST,
      url: EndPoints.UpdateCustomBlockPage,
      data,
    })

    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getSubscriptionDetails = async (): Promise<
  AxiosResponse<SubscriptionDetailsType>
> => {
  try {
    return await requestHandler<SubscriptionDetailsType>({
      method: HttpMethods.GET,
      url: EndPoints.SubscriptionDetails,
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const auditBaseUrl = getAuditBaseUrl()

const getAuditLogs = async (
  request: AuditLogsRequestType,
): Promise<AxiosResponse<AuditLogsType>> => {
  try {
    return await requestHandler<AuditLogsType>({
      method: HttpMethods.POST,
      url: auditBaseUrl + EndPoints.GetAuditLogs,
      data: {
        requestQuery: {
          ...request,
          limit: 50,
        },
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getUserGroupDetails = async (
  email: string,
): Promise<AxiosResponse<GetUserGroupDetailsResponse>> => {
  try {
    return await requestHandler<GetUserGroupDetailsResponse>({
      method: HttpMethods.POST,
      url: EndPoints.GetUserGroupDetails,
      data: {
        user: email,
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

export const SettingsService = {
  getLink,
  getGroups,
  getUsers,
  getInvitedAdmin,
  inviteAdmins,
  verifyAdminInvite,
  deleteAdmin,
  getImportStatus,
  getSearchUsersGroups,
  getBlockPages,
  updateDefaultBlockPages,
  getEndpointsConfig,
  updateEndpointsConfig,
  updateCustomBlockPages,
  getSubscriptionDetails,
  getAuditLogs,
  getUserGroupDetails,
  updateInvitedAdmins,
}
